import React, { useState, useEffect, useRef, useMemo } from "react";
import Cookie from "js-cookie";
import {
    createLocation,
    searchCountries,
    searchStatesByCountry,
    searchCitiesByCountryState,
    saveNewCity,
} from "../api";

const LOCATION_TYPES = [
    { value: "POINT", label: "Point (general facility)" },
    { value: "WAREHOUSE", label: "Warehouse" },
    { value: "OCEAN_TERMINAL", label: "Ocean Terminal" },
    { value: "RAIL_TERMINAL", label: "Rail Terminal" },
    { value: "OTHER", label: "Other" },
];

const initialState = {
    locationName: "",
    locationType: "POINT",
    customLocationIdentifier: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    cityName: "",
    state: "",
    stateName: "",
    zipCode: "",
    country: "",
    countryName: "",
    latitude: "",
    longitude: "",
    appointmentUrl: "",
    contactName: "",
    phoneNumber: "",
    email: "",
    fax: "",
};

const validateEmail = (email) => !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const validatePhone = (phone) => !phone || /^\+?[0-9]{10,15}$/.test(phone);

// Normalizes whatever shape the geography search endpoints return into {code, name}
const normalizeOptions = (raw, codeKeys, nameKeys) => {
    const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
    const seen = new Set();
    return list
        .map((item) => {
            if (!item) return null;
            if (typeof item === "string") return { code: item, name: item };
            const code = codeKeys.map((k) => item[k]).find(Boolean) || item.name;
            const name = nameKeys.map((k) => item[k]).find(Boolean) || code;
            return { code, name };
        })
        .filter(Boolean)
        .filter((o) => {
            const key = `${(o.name || "").toLowerCase()}::${(o.code || "").toLowerCase()}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        })
        .sort((a, b) => String(a.name).localeCompare(String(b.name)));
};

export default function CreateLocationTab({ onClose, onSuccess }) {
    const [form, setForm] = useState(initialState);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const token = Cookie.get("authToken") || null;
    const officeCode = Cookie.get("officeCode");

    // --- Country typeahead ---
    const [countryQuery, setCountryQuery] = useState("");
    const [countryOptions, setCountryOptions] = useState([]);
    const [countryOpen, setCountryOpen] = useState(false);
    const [countryLoading, setCountryLoading] = useState(false);
    const [countryActiveIdx, setCountryActiveIdx] = useState(-1);
    const countryListRef = useRef(null);
    const countryAbortRef = useRef(null);

    // --- State typeahead (depends on country) ---
    const [stateQuery, setStateQuery] = useState("");
    const [stateOptions, setStateOptions] = useState([]);
    const [stateOpen, setStateOpen] = useState(false);
    const [stateLoading, setStateLoading] = useState(false);
    const [stateActiveIdx, setStateActiveIdx] = useState(-1);
    const stateListRef = useRef(null);
    const stateAbortRef = useRef(null);

    // --- City typeahead (depends on country + state) ---
    const [cityQuery, setCityQuery] = useState("");
    const [cityOptions, setCityOptions] = useState([]);
    const [cityOpen, setCityOpen] = useState(false);
    const [cityLoading, setCityLoading] = useState(false);
    const [cityActiveIdx, setCityActiveIdx] = useState(-1);
    const cityListRef = useRef(null);
    const cityAbortRef = useRef(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        if (name === "phoneNumber" || name === "email") setError("");
    };

    // ---------------- Country ----------------
    const runCountrySearch = async (query) => {
        countryAbortRef.current?.abort?.();
        const controller = new AbortController();
        countryAbortRef.current = controller;
        try {
            setCountryLoading(true);
            const resp = await searchCountries(query);
            const opts = normalizeOptions(resp.data, ["code", "countryCode", "isoCode"], ["name", "countryName"]);
            setCountryOptions(opts);
            setCountryOpen(true);
            setCountryActiveIdx(opts.length ? 0 : -1);
        } catch (err) {
            if (err?.name === "CanceledError") return;
            setCountryOptions([]);
            setCountryOpen(true);
            setCountryActiveIdx(-1);
        } finally {
            setCountryLoading(false);
        }
    };

    useEffect(() => {
        const q = (countryQuery || form.countryName || "").trim();
        if (q.length < 2) { setCountryOptions([]); setCountryOpen(false); return; }
        const id = setTimeout(() => runCountrySearch(q), 300);
        return () => clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countryQuery]);

    useEffect(() => {
        const onDocClick = (e) => {
            if (countryListRef.current && !countryListRef.current.contains(e.target)) setCountryOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const handleCountrySelect = (opt) => {
        const code = opt?.code || "";
        const name = opt?.name || code;
        setForm((p) => ({
            ...p,
            country: code || name,
            countryName: name,
            state: "", stateName: "",
            city: "", cityName: "",
        }));
        setCountryQuery("");
        setCountryOpen(false);
        setCountryActiveIdx(-1);
        setStateQuery(""); setStateOptions([]); setStateOpen(false);
        setCityQuery(""); setCityOptions([]); setCityOpen(false);
    };

    // ---------------- State ----------------
    const runStateSearch = async (query, countryCode) => {
        if (!countryCode) return;
        stateAbortRef.current?.abort?.();
        const controller = new AbortController();
        stateAbortRef.current = controller;
        try {
            setStateLoading(true);
            const resp = await searchStatesByCountry(countryCode, query);
            const opts = normalizeOptions(resp.data, ["code", "stateCode", "isoCode"], ["name", "stateName"]);
            setStateOptions(opts);
            setStateOpen(true);
            setStateActiveIdx(opts.length ? 0 : -1);
        } catch (err) {
            if (err?.name === "CanceledError") return;
            setStateOptions([]);
            setStateOpen(true);
            setStateActiveIdx(-1);
        } finally {
            setStateLoading(false);
        }
    };

    useEffect(() => {
        const q = (stateQuery || form.stateName || "").trim();
        if (q.length < 2 || !form.country) { setStateOptions([]); setStateOpen(false); return; }
        const id = setTimeout(() => runStateSearch(q, form.country), 300);
        return () => clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateQuery, form.country]);

    useEffect(() => {
        const onDocClick = (e) => {
            if (stateListRef.current && !stateListRef.current.contains(e.target)) setStateOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const handleStateSelect = (opt) => {
        const code = opt?.code || "";
        const name = opt?.name || code;
        setForm((p) => ({ ...p, state: code || name, stateName: name, city: "", cityName: "" }));
        setStateQuery("");
        setStateOpen(false);
        setStateActiveIdx(-1);
        setCityQuery(""); setCityOptions([]); setCityOpen(false);
    };

    // ---------------- City ----------------
    const runCitySearch = async (query, countryCode, stateCode) => {
        if (!countryCode || !stateCode) return;
        cityAbortRef.current?.abort?.();
        const controller = new AbortController();
        cityAbortRef.current = controller;
        try {
            setCityLoading(true);
            const resp = await searchCitiesByCountryState(countryCode, stateCode, query);
            const opts = normalizeOptions(resp.data, ["code", "cityCode"], ["name", "cityName"]);
            setCityOptions(opts);
            setCityOpen(true);
            setCityActiveIdx(opts.length ? 0 : -1);
        } catch (err) {
            if (err?.name === "CanceledError") return;
            setCityOptions([]);
            setCityOpen(true);
            setCityActiveIdx(-1);
        } finally {
            setCityLoading(false);
        }
    };

    useEffect(() => {
        const q = (cityQuery || form.cityName || "").trim();
        if (q.length < 1 || !form.country || !form.state) { setCityOptions([]); setCityOpen(false); return; }
        const id = setTimeout(() => runCitySearch(q, form.country, form.state), 300);
        return () => clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cityQuery, form.country, form.state]);

    useEffect(() => {
        const onDocClick = (e) => {
            if (cityListRef.current && !cityListRef.current.contains(e.target)) setCityOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const handleCitySelect = (opt) => {
        const code = opt?.code || "";
        const name = opt?.name || code;
        setForm((p) => ({ ...p, city: code || name, cityName: name }));
        setCityQuery("");
        setCityOpen(false);
        setCityActiveIdx(-1);
    };

    // Persist a typed city that has no existing match, so it's available next time.
    const persistNewCityIfNeeded = async (typedName) => {
        const trimmed = (typedName || "").trim();
        if (!trimmed || !form.country || !form.state) return;
        const exact = cityOptions.find((o) => (o.name || "").toLowerCase() === trimmed.toLowerCase());
        if (exact) return;
        try {
            const resp = await saveNewCity(form.country, form.state, trimmed);
            if (resp?.data) {
                setForm((p) => ({ ...p, city: resp.data.cityName || trimmed, cityName: resp.data.cityName || trimmed }));
            }
        } catch {
            // Non-fatal — the manually-typed city/state/country still submits as free text.
        }
    };

    const visibleCountryOptions = useMemo(() => countryOptions, [countryOptions]);
    const visibleStateOptions = useMemo(() => stateOptions, [stateOptions]);
    const visibleCityOptions = useMemo(() => cityOptions, [cityOptions]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateEmail(form.email)) {
            setError("Invalid email format");
            return;
        }
        if (!validatePhone(form.phoneNumber)) {
            setError("Invalid phone number. Please enter a valid phone number (10-15 digits)");
            return;
        }
        setError("");
        setLoading(true);
        try {
            const payload = {
                ...form,
                country: form.country || countryQuery,
                state: form.state || stateQuery,
                city: form.city || cityQuery,
                latitude: form.latitude === "" ? null : Number(form.latitude),
                longitude: form.longitude === "" ? null : Number(form.longitude),
            };
            await createLocation(token, officeCode, payload);
            window.dispatchEvent(new Event("location:refresh"));
            onSuccess?.();
            onClose?.();
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to create location");
        } finally {
            setLoading(false);
        }
    };

    const inputClass = "border rounded-md p-2 w-full dark:bg-acs-dark-surface dark:text-acs-dark-text dark:border-acs-dark-border";
    const dropdownClass = "absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface shadow-lg";
    const optionClass = (active) => `px-4 py-2 cursor-pointer text-sm ${active ? "bg-[#e8edff] dark:bg-acs-dark-border text-[#223688] dark:text-acs-dark-text" : "hover:bg-gray-50 dark:hover:bg-acs-dark-border dark:text-acs-dark-text"}`;

    return (
        <div className="w-full h-full min-h-0 flex flex-col overflow-y-auto bg-white dark:bg-acs-dark-surface p-fluid-1">
            <div className="bg-white dark:bg-acs-dark-surface rounded-[clamp(0.75rem,1vw,1.5rem)] shadow-fluid-card p-fluid-2">
                <h2 className="text-fluid-xl font-semibold mb-6 text-gray-800 dark:text-acs-dark-text">Create Location</h2>

                <form onSubmit={handleSubmit} className="space-y-8">
                    <div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="block font-medium mb-1 dark:text-acs-dark-text">Location Name *</label>
                                <input type="text" name="locationName" value={form.locationName} onChange={handleChange} required
                                       className={inputClass} placeholder="Enter location name" />
                            </div>
                            <div>
                                <label className="block font-medium mb-1 dark:text-acs-dark-text">Location Type *</label>
                                <select name="locationType" value={form.locationType} onChange={handleChange} required className={inputClass}>
                                    {LOCATION_TYPES.map((t) => (<option key={t.value} value={t.value}>{t.label}</option>))}
                                </select>
                            </div>
                            <div>
                                <label className="block font-medium mb-1 dark:text-acs-dark-text">ERP Identifier</label>
                                <input type="text" name="customLocationIdentifier" value={form.customLocationIdentifier} onChange={handleChange}
                                       className={inputClass} placeholder="Shipper's own code for this location, if any" />
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-fluid-xl font-semibold mb-4 text-gray-800 dark:text-acs-dark-text">Address Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="md:col-span-2">
                                <label className="block font-medium mb-1 dark:text-acs-dark-text">Address Line 1 *</label>
                                <input type="text" name="addressLine1" value={form.addressLine1} onChange={handleChange} required
                                       className={inputClass} placeholder="Enter address line 1" />
                            </div>
                            <div>
                                <label className="block font-medium mb-1 dark:text-acs-dark-text">Address Line 2</label>
                                <input type="text" name="addressLine2" value={form.addressLine2} onChange={handleChange}
                                       className={inputClass} placeholder="Enter address line 2" />
                            </div>

                            {/* Country — typeahead */}
                            <div className="relative" ref={countryListRef}>
                                <label className="block font-medium mb-1 dark:text-acs-dark-text">Country *</label>
                                <input
                                    type="text"
                                    autoComplete="off"
                                    value={countryQuery || form.countryName}
                                    onFocus={() => setCountryOpen(visibleCountryOptions.length > 0)}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setCountryQuery(val);
                                        setForm((p) => ({ ...p, countryName: val, country: val }));
                                        setCountryOpen(true);
                                        setCountryActiveIdx(-1);
                                    }}
                                    onKeyDown={(e) => {
                                        if (!countryOpen) return;
                                        if (e.key === "ArrowDown") { e.preventDefault(); setCountryActiveIdx((i) => Math.min(visibleCountryOptions.length - 1, i + 1)); }
                                        else if (e.key === "ArrowUp") { e.preventDefault(); setCountryActiveIdx((i) => Math.max(0, i - 1)); }
                                        else if (e.key === "Enter" && countryActiveIdx >= 0) { e.preventDefault(); handleCountrySelect(visibleCountryOptions[countryActiveIdx]); }
                                        else if (e.key === "Escape" || e.key === "Tab") setCountryOpen(false);
                                    }}
                                    required
                                    className={inputClass}
                                    placeholder="Start typing a country"
                                />
                                {countryOpen && (countryLoading || visibleCountryOptions.length > 0) && (
                                    <ul role="listbox" className={dropdownClass}>
                                        {countryLoading && <li className="px-4 py-2 text-sm text-gray-500 dark:text-acs-dark-subtext">Searching…</li>}
                                        {!countryLoading && visibleCountryOptions.map((opt, idx) => (
                                            <li key={`${opt.code}-${idx}`} role="option" aria-selected={idx === countryActiveIdx}
                                                className={optionClass(idx === countryActiveIdx)}
                                                onMouseEnter={() => setCountryActiveIdx(idx)}
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => handleCountrySelect(opt)}>
                                                {opt.name}{opt.code && opt.code !== opt.name ? ` (${opt.code})` : ""}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* State — typeahead, depends on country */}
                            <div className="relative" ref={stateListRef}>
                                <label className="block font-medium mb-1 dark:text-acs-dark-text">State *</label>
                                <input
                                    type="text"
                                    autoComplete="off"
                                    disabled={!form.country}
                                    value={stateQuery || form.stateName}
                                    onFocus={() => setStateOpen(visibleStateOptions.length > 0)}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setStateQuery(val);
                                        setForm((p) => ({ ...p, stateName: val, state: val }));
                                        setStateOpen(true);
                                        setStateActiveIdx(-1);
                                    }}
                                    onKeyDown={(e) => {
                                        if (!stateOpen) return;
                                        if (e.key === "ArrowDown") { e.preventDefault(); setStateActiveIdx((i) => Math.min(visibleStateOptions.length - 1, i + 1)); }
                                        else if (e.key === "ArrowUp") { e.preventDefault(); setStateActiveIdx((i) => Math.max(0, i - 1)); }
                                        else if (e.key === "Enter" && stateActiveIdx >= 0) { e.preventDefault(); handleStateSelect(visibleStateOptions[stateActiveIdx]); }
                                        else if (e.key === "Escape" || e.key === "Tab") setStateOpen(false);
                                    }}
                                    required
                                    className={`${inputClass} disabled:opacity-50 disabled:cursor-not-allowed`}
                                    placeholder={form.country ? "Start typing a state" : "Select a country first"}
                                />
                                {stateOpen && (stateLoading || visibleStateOptions.length > 0) && (
                                    <ul role="listbox" className={dropdownClass}>
                                        {stateLoading && <li className="px-4 py-2 text-sm text-gray-500 dark:text-acs-dark-subtext">Searching…</li>}
                                        {!stateLoading && visibleStateOptions.map((opt, idx) => (
                                            <li key={`${opt.code}-${idx}`} role="option" aria-selected={idx === stateActiveIdx}
                                                className={optionClass(idx === stateActiveIdx)}
                                                onMouseEnter={() => setStateActiveIdx(idx)}
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => handleStateSelect(opt)}>
                                                {opt.name}{opt.code && opt.code !== opt.name ? ` (${opt.code})` : ""}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* City — typeahead, depends on country + state, falls back to manual add */}
                            <div className="relative" ref={cityListRef}>
                                <label className="block font-medium mb-1 dark:text-acs-dark-text">City *</label>
                                <input
                                    type="text"
                                    autoComplete="off"
                                    disabled={!form.country || !form.state}
                                    value={cityQuery || form.cityName}
                                    onFocus={() => setCityOpen(visibleCityOptions.length > 0)}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setCityQuery(val);
                                        setForm((p) => ({ ...p, cityName: val, city: val }));
                                        setCityOpen(true);
                                        setCityActiveIdx(-1);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            if (cityActiveIdx >= 0) { e.preventDefault(); handleCitySelect(visibleCityOptions[cityActiveIdx]); }
                                            else if (cityQuery.trim().length > 0) { e.preventDefault(); persistNewCityIfNeeded(cityQuery); setCityOpen(false); }
                                        } else if (!cityOpen) return;
                                        else if (e.key === "ArrowDown") { e.preventDefault(); setCityActiveIdx((i) => Math.min(visibleCityOptions.length - 1, i + 1)); }
                                        else if (e.key === "ArrowUp") { e.preventDefault(); setCityActiveIdx((i) => Math.max(0, i - 1)); }
                                        else if (e.key === "Escape" || e.key === "Tab") setCityOpen(false);
                                    }}
                                    onBlur={() => {
                                        setTimeout(() => {
                                            if (cityQuery.trim().length > 0) persistNewCityIfNeeded(cityQuery);
                                            setCityOpen(false);
                                        }, 200);
                                    }}
                                    required
                                    className={`${inputClass} disabled:opacity-50 disabled:cursor-not-allowed`}
                                    placeholder={form.state ? "Start typing a city, or type a new one" : "Select a state first"}
                                />
                                {cityOpen && (cityLoading || visibleCityOptions.length > 0) && (
                                    <ul role="listbox" className={dropdownClass}>
                                        {cityLoading && <li className="px-4 py-2 text-sm text-gray-500 dark:text-acs-dark-subtext">Searching…</li>}
                                        {!cityLoading && visibleCityOptions.map((opt, idx) => (
                                            <li key={`${opt.code}-${idx}`} role="option" aria-selected={idx === cityActiveIdx}
                                                className={optionClass(idx === cityActiveIdx)}
                                                onMouseEnter={() => setCityActiveIdx(idx)}
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => handleCitySelect(opt)}>
                                                {opt.name}
                                            </li>
                                        ))}
                                        {!cityLoading && visibleCityOptions.length === 0 && cityQuery.trim().length > 0 && (
                                            <li className="px-4 py-2 text-sm text-gray-500 dark:text-acs-dark-subtext">
                                                No match — "{cityQuery.trim()}" will be added as a new city
                                            </li>
                                        )}
                                    </ul>
                                )}
                            </div>

                            <div>
                                <label className="block font-medium mb-1 dark:text-acs-dark-text">Zip Code *</label>
                                <input type="text" name="zipCode" value={form.zipCode} onChange={handleChange} required
                                       className={inputClass} placeholder="Enter zip code" />
                            </div>
                            <div>
                                <label className="block font-medium mb-1 dark:text-acs-dark-text">Latitude</label>
                                <input type="number" step="any" name="latitude" value={form.latitude} onChange={handleChange}
                                       className={inputClass} placeholder="Auto-detected from address if left blank" />
                            </div>
                            <div>
                                <label className="block font-medium mb-1 dark:text-acs-dark-text">Longitude</label>
                                <input type="number" step="any" name="longitude" value={form.longitude} onChange={handleChange}
                                       className={inputClass} placeholder="Auto-detected from address if left blank" />
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-fluid-xl font-semibold mb-4 text-gray-800 dark:text-acs-dark-text">Contact Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="block font-medium mb-1 dark:text-acs-dark-text">Contact Name</label>
                                <input type="text" name="contactName" value={form.contactName} onChange={handleChange}
                                       className={inputClass} placeholder="Enter contact name" />
                            </div>
                            <div>
                                <label className="block font-medium mb-1 dark:text-acs-dark-text">Phone Number</label>
                                <input type="text" name="phoneNumber" value={form.phoneNumber} onChange={handleChange}
                                       className={`${inputClass} ${error && form.phoneNumber ? "border-red-500" : ""}`} placeholder="e.g. +1-555-0100" />
                            </div>
                            <div>
                                <label className="block font-medium mb-1 dark:text-acs-dark-text">Email</label>
                                <input type="email" name="email" value={form.email} onChange={handleChange}
                                       className={`${inputClass} ${error && form.email ? "border-red-500" : ""}`} placeholder="Enter email" />
                            </div>
                            <div>
                                <label className="block font-medium mb-1 dark:text-acs-dark-text">Fax</label>
                                <input type="text" name="fax" value={form.fax} onChange={handleChange}
                                       className={inputClass} placeholder="Enter fax" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block font-medium mb-1 dark:text-acs-dark-text">Appointment URL</label>
                                <input type="text" name="appointmentUrl" value={form.appointmentUrl} onChange={handleChange}
                                       className={inputClass} placeholder="Enter appointment URL" />
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="text-red-600 text-sm flex items-center bg-red-50 dark:bg-red-950/40 p-3 rounded-md">
                            <span className="mr-2">&#9888;</span>
                            {error}
                        </div>
                    )}

                    <div className="flex justify-end gap-4 pt-6 border-t border-gray-200 dark:border-acs-dark-border">
                        <button type="button" onClick={onClose}
                                className="px-6 py-2 border border-gray-300 dark:border-acs-dark-border rounded-md text-gray-700 dark:text-acs-dark-text hover:bg-gray-100 dark:hover:bg-acs-dark-border transition-colors font-medium">
                            Cancel
                        </button>
                        <button type="submit" disabled={loading}
                                className="px-6 py-2 bg-[#1e2a74] text-white hover:bg-[#18225c] rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            {loading ? "Creating..." : "Create Location"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
